/**
 * iter 144n - Capture `?promo=<code>` from any landing URL and persist
 * it to localStorage so the discount survives navigation between the
 * landing page and the Blueprint intake/checkout flow.
 *
 *  - Captures on first visit only (first-touch). If a stored promo is
 *    still within its TTL, we don't overwrite it with a new URL value;
 *    if it's expired, the new value replaces it.
 *  - TTL: 30 days, mirrors the UTM capture window.
 *  - Empty / whitespace-only values are ignored.
 *  - On checkout, `getStoredPromo()` returns the bare code string (e.g.
 *    `"LAUNCH25"`) and the backend `/api/blueprint/checkout` route
 *    validates it against Stripe before attaching as a discount. Invalid
 *    codes are silently dropped server-side, so this never blocks
 *    checkout.
 */
const KEY = 'mundalo_promo_code';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

function _safeRead() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.code || !obj.t) return null;
    if (Date.now() - Number(obj.t) > TTL_MS) return null;
    return obj;
  } catch { return null; }
}

function _safeWrite(code) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ code, t: Date.now() }));
  } catch { /* storage blocked */ }
}

export function capturePromoFromUrl() {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = (params.get('promo') || '').trim();
    if (!raw) return _safeRead()?.code || null;
    // First-touch wins - don't overwrite a still-valid stored code with
    // a different one from a later visit.
    const existing = _safeRead();
    if (existing && existing.code && existing.code.toUpperCase() === raw.toUpperCase()) {
      return existing.code;
    }
    _safeWrite(raw);
    return raw;
  } catch { return null; }
}

export function getStoredPromo() {
  return _safeRead()?.code || null;
}
